<button
  type="button"
  class="drf-trigger"
  [class.icon-only]="iconOnly"
  [class.has-range]="iconOnly && isCustom"
  cdkOverlayOrigin
  #origin="cdkOverlayOrigin"
  [attr.aria-expanded]="open"
  [attr.aria-label]="iconOnly ? summary.name + (summary.dates ? ' ' + summary.dates : '') : null"
  [attr.title]="iconOnly && summary.dates ? summary.dates : null"
  aria-haspopup="dialog"
  (click)="toggle()"
>
  @if (iconOnly) {
    <!-- Compact trigger (e.g. the Tasks reminders header): a calendar icon; tinted while a range is applied. -->
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M7 2v2H5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2V2h-2v2H9V2H7zm-2 7h14v10H5V9zm2 2v2h2v-2H7zm4 0v2h2v-2h-2zm4 0v2h2v-2h-2z"
        fill="currentColor"
      />
    </svg>
  } @else {
    @if (prefix) {
      <span class="drf-prefix">{{ prefix }}</span>
    }
    <span class="drf-name">{{ summary.name }}</span>
    @if (summary.dates) {
      <span class="drf-dates">{{ summary.dates }}</span>
    }
    <svg width="11" height="11" viewBox="0 0 11 11" aria-hidden="true">
      <path d="M5.5 8.2 1 3.7l.9-.9 3.6 3.6 3.6-3.6.9.9z" fill="currentColor" />
    </svg>
  }
</button>
@if (showReset) {
  <!-- Card #269: the custom range is remembered; this puts the screen back on its default. -->
  <button
    type="button"
    class="drf-reset"
    [attr.aria-label]="'DATE_RANGE.CLEAR_CUSTOM' | translate"
    [attr.title]="'DATE_RANGE.CLEAR_CUSTOM' | translate"
    (click)="clear()"
  >
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
      <path d="M1.5 1.5l7 7m0-7l-7 7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
    </svg>
    <span>{{ 'DATE_RANGE.CLEAR' | translate }}</span>
  </button>
}

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="origin"
  [cdkConnectedOverlayOpen]="open"
  [cdkConnectedOverlayPositions]="positions"
  [cdkConnectedOverlayPush]="true"
  [cdkConnectedOverlayViewportMargin]="12"
  cdkConnectedOverlayPanelClass="drf-overlay"
  (overlayOutsideClick)="outsideClick($event, origin.elementRef.nativeElement)"
  (detach)="close()"
  (overlayKeydown)="$event.key === 'Escape' && close()"
>
  <div class="drf-panel" [class.no-presets]="!presets.length" role="dialog" [attr.aria-label]="'DATE_RANGE.TITLE' | translate">
    <!-- No presets (e.g. a chart's "Custom Date" comparison, whose presets live in its own dropdown): custom range only. -->
    @if (presets.length) {
      <div class="drf-presets">
        <span class="drf-presets-title">{{ 'DATE_RANGE.QUICK' | translate }}</span>
        @for (p of presets; track p.value) {
          <button
            type="button"
            class="drf-preset"
            [class.active]="!isCustom && p.value === value"
            [attr.aria-pressed]="!isCustom && p.value === value"
            (click)="choosePreset(p.value)"
          >
            <span>{{ p.name }}</span>
            @if (p.dates) {
              <small>{{ p.dates }}</small>
            }
          </button>
        }
      </div>
    }

    <div class="drf-custom">
      <span class="drf-presets-title">{{ 'DATE_RANGE.CUSTOM_RANGE' | translate }}</span>
      <div class="drf-fields">
        <label class="drf-field" [class.active]="setting === 'start'" [class.bad]="box.start === 'bad'">
          <span>{{ 'DATE_RANGE.START' | translate }}</span>
          <input
            #startBox
            autocomplete="off"
            placeholder="dd/mm/yyyy"
            inputmode="decimal"
            [value]="startText"
            (focus)="focusBox('start')"
            (input)="maskTyping('start', $any($event.target), endBox, $event)"
            (compositionend)="maskTyping('start', $any($event.target), endBox)"
            (change)="blurApply('start', $any($event.target).value)"
          />
        </label>
        <span class="drf-arrow" aria-hidden="true">→</span>
        <label class="drf-field" [class.active]="setting === 'end'" [class.bad]="box.end === 'bad'">
          <span>{{ 'DATE_RANGE.END' | translate }}</span>
          <input
            #endBox
            autocomplete="off"
            placeholder="dd/mm/yyyy"
            inputmode="decimal"
            [value]="endText"
            (focus)="focusBox('end')"
            (input)="maskTyping('end', $any($event.target), undefined, $event)"
            (compositionend)="maskTyping('end', $any($event.target))"
            (change)="blurApply('end', $any($event.target).value)"
          />
        </label>
      </div>
      <p class="drf-hint" aria-live="polite">{{ hint }}</p>

      <div class="drf-months" (mouseleave)="setHover(null)">
        @for (m of months; track $index; let first = $first; let mi = $index) {
          <div class="drf-month" [class.second]="!first">
            <div class="drf-mhead">
              <button
                type="button"
                class="drf-nav"
                [class.hide-wide]="!first && !apart"
                [attr.aria-label]="'DATE_RANGE.PREV_MONTH' | translate"
                (click)="step(mi, -1)"
              >‹</button>
              <span class="drf-mtitle">
                <select
                  class="drf-jump"
                  [attr.aria-label]="('DATE_RANGE.MONTH' | translate) + ' – ' + m.title"
                  (change)="jumpTo(mi, m.year, $any($event.target).value, $any($event.target))"
                >
                  @for (name of monthLabels; track $index) {
                    <option [value]="$index" [selected]="$index === m.month">{{ name }}</option>
                  }
                </select>
                <select
                  class="drf-jump"
                  [attr.aria-label]="('DATE_RANGE.YEAR' | translate) + ' – ' + m.title"
                  (change)="jumpTo(mi, $any($event.target).value, m.month, $any($event.target))"
                >
                  @for (y of years; track y) {
                    <option [value]="y" [selected]="y === m.year">{{ y }}</option>
                  }
                </select>
              </span>
              <button
                type="button"
                class="drf-nav"
                [class.hide-wide]="first && !apart"
                [attr.aria-label]="'DATE_RANGE.NEXT_MONTH' | translate"
                (click)="step(mi, 1)"
              >›</button>
            </div>
            <div class="drf-grid">
              @for (w of weekdays; track $index) {
                <span class="drf-dow">{{ w }}</span>
              }
              @for (c of m.cells; track trackCell($index, c)) {
                @if (c.date) {
                  <button
                    type="button"
                    class="drf-day"
                    [ngClass]="dayClass(c.date)"
                    [attr.aria-label]="formatDate(c.date)"
                    (click)="pickDay(c.date)"
                    (mouseenter)="setHover(c.date)"
                  >
                    <span>{{ c.date.getDate() }}</span>
                  </button>
                } @else {
                  <span class="drf-day blank"></span>
                }
              }
            </div>
          </div>
        }
      </div>

      <div class="drf-foot">
        <span class="drf-span">{{ spanText }}</span>
        <div class="drf-btns">
          @if (showClear) {
            <button type="button" class="drf-btn" (click)="clear()">{{ 'DATE_RANGE.CLEAR' | translate }}</button>
          }
          <button type="button" class="drf-btn" (click)="close()">{{ 'COMMON.Cancel' | translate }}</button>
          <button type="button" class="drf-btn primary" [disabled]="!canApply" (click)="apply()">
            {{ 'COMMON.Apply' | translate }}
          </button>
        </div>
      </div>
    </div>
  </div>
</ng-template>
